import { BulletedListElement, ListItemElement, NumberedListElement } from '@typedream/components';
import { DreamBlock, DreamValue } from '@typedream/core';
import { createAutoformatPlugin } from '@udecode/plate-autoformat';
import { PluginOptions, TEditor } from '@udecode/plate-core';
import { createTElementPluginFactory, createTPluginFactory } from '../../plate';
import {
  ELEMENT_BUTTON,
  ELEMENT_CONTAINER,
  ELEMENT_DIVIDER,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_IMAGE,
  ELEMENT_LI,
  ELEMENT_LIC,
  ELEMENT_MEDIA_EMBED,
  ELEMENT_OL,
  ELEMENT_PARAGRAPH,
  ELEMENT_SPACER,
  ELEMENT_TODO_LI,
  ELEMENT_UL,
  KEY_ALL_LIST,
} from '../../pluginKeys';
import { withDropTarget } from '../../plugins/dnd';
import { onKeyDownList } from './onKeyDownList';
import { ListPlugin } from './types';
import { withList } from './withList';
import { BaseDreamEditor, DreamEditor } from '../../types';
import { toggleList, unwrapList } from './transforms';
import { autoFormatQuery } from './queries/autoformat';
import { createTodoListPlugin } from './createTodoListPlugin';

const preFormat = (editor: TEditor) => {
  unwrapList(editor as unknown as DreamEditor);
};

const createUnorderedListAutoFormatterPlugin = () =>
  createAutoformatPlugin<PluginOptions, DreamValue, BaseDreamEditor>({
    key: 'autoformat-unordered-list',
    options: {
      rules: [
        {
          mode: 'block',
          type: ELEMENT_UL,
          match: ['* ', '- '],
          query: autoFormatQuery,
          preFormat,
          // there is always issue when typing editor with other than any
          format: (editor: any) => toggleList(editor, { type: ELEMENT_UL }),
        },
      ],
    },
  });

const createBulletedListPlugin = createTElementPluginFactory({
  key: ELEMENT_UL,
  component: withDropTarget(BulletedListElement),
  options: {
    isDreamBlock: true,
    isSelectable: true,
    isResizable: true,
    isDraggable: true,
    isDroppable: true,
    meta: {
      img: '/canvas/images/bulleted_list.png',
      title: 'Bulleted List',
      subtitle: 'Make a bulleted list',
    },
    format: {
      formatType: 'list',
      turnInto: [ELEMENT_OL, ELEMENT_TODO_LI],
      focusableWhenEmpty: true,
      validProperties: ['width', 'width_max', 'mobile_hide', 'desktop_hide'],
      validChildrenTypes: [ELEMENT_LI],
    },
  },
  plugins: [createUnorderedListAutoFormatterPlugin()],
});

const createOrderedListAutoFormatterPlugin = () =>
  createAutoformatPlugin<PluginOptions, DreamValue, BaseDreamEditor>({
    key: 'autoformat-ordered-list',
    options: {
      rules: [
        {
          mode: 'block',
          type: ELEMENT_OL,
          match: ['1. ', '1) '],
          query: autoFormatQuery,
          preFormat,
          format: (editor: any) => toggleList(editor, { type: ELEMENT_OL }),
        },
      ],
    },
  });

const createNumberedListPlugin = createTElementPluginFactory({
  key: ELEMENT_OL,
  component: withDropTarget(NumberedListElement),
  options: {
    isDreamBlock: true,
    isSelectable: true,
    isResizable: true,
    isDraggable: true,
    isDroppable: true,
    meta: {
      img: '/canvas/images/numbered_list.png',
      title: 'Numbered List',
      subtitle: 'Make a list with numbering',
    },
    format: {
      formatType: 'list',
      turnInto: [ELEMENT_UL, ELEMENT_TODO_LI],
      focusableWhenEmpty: true,
      validProperties: ['width', 'width_max', 'mobile_hide', 'desktop_hide'],
      validChildrenTypes: [ELEMENT_LI],
    },
  },
  plugins: [createOrderedListAutoFormatterPlugin()],
});

const validLiChildrenTypes = [
  ELEMENT_PARAGRAPH,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_IMAGE,
  ELEMENT_BUTTON,
  ELEMENT_CONTAINER,
  ELEMENT_MEDIA_EMBED,
  ELEMENT_DIVIDER,
  ELEMENT_SPACER,
  ELEMENT_OL,
  ELEMENT_UL,
  ELEMENT_LIC,
] as DreamBlock['type'][];

const createListItemPlugin = createTElementPluginFactory({
  key: ELEMENT_LI,
  component: withDropTarget(ListItemElement),
  options: {
    isSelectable: true,
    isDraggable: true,
    isDroppable: true,
    meta: {
      title: 'List Item',
    },
    format: {
      formatType: 'list',
      turnInto: [ELEMENT_OL, ELEMENT_UL, ELEMENT_TODO_LI],
      focusableWhenEmpty: true,
      validProperties: ['mobile_hide', 'desktop_hide', 'connect_data'],
      validChildrenTypes: validLiChildrenTypes,
    },
  },
});

const createListItemContentPlugin = createTPluginFactory({
  key: ELEMENT_LIC,
  isElement: true,
});

export const createListPlugin = createTPluginFactory<ListPlugin>({
  key: KEY_ALL_LIST,
  handlers: {
    onKeyDown: onKeyDownList,
  },
  options: {
    validLiChildrenTypes,
  },
  withOverrides: withList,
  plugins: [
    createBulletedListPlugin(),
    createNumberedListPlugin(),
    createTodoListPlugin(),
    createListItemPlugin(),
    createListItemContentPlugin(),
  ],
});
